import React, { useEffect, useState } from "react";
import axios from "axios";
import ReactDOM from "react-dom";
import "./EditOrganMenuModal.css";

function EditOrganMenuModal({ modal, setModal, id, materials, onMaterialsUpdated }) {
  const [dragIndex, setDragIndex] = useState(null);
  const [materialsUI, setMaterialsUI] = useState([]);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (modal) {
      setMaterialsUI(Array.isArray(materials) ? materials : []);
      setErrorMessage("");
    }
  }, [modal, materials]);

  const closeModal = () => {
    if (!saving) {
      setModal({ show: false, id: null });
    }
  };

  const handleDrop = (dropIndex) => {
    if (dragIndex === null || dragIndex === dropIndex) return;

    const updated = [...materialsUI];
    const [movedItem] = updated.splice(dragIndex, 1);
    updated.splice(dropIndex, 0, movedItem);
    setMaterialsUI(updated);
    setDragIndex(null);
  };

  const updateDisplayName = (index, value) => {
    setMaterialsUI((previous) =>
      previous.map((item, itemIndex) =>
        itemIndex === index ? { ...item, displayName: value } : item
      )
    );
  };

  const handleSubmit = async () => {
    const materialConfig = materialsUI.map((material, order) => ({
      originalName: material.originalName,
      displayName: material.displayName,
      order,
    }));

    setSaving(true);
    setErrorMessage("");

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/update-glb-model-material`,
        {
          _id: id,
          materials: materialConfig.length ? materialConfig : undefined,
        }
      );

      await onMaterialsUpdated(response.data.materials);
      setModal({ show: false, id: null });
    } catch (error) {
      console.error("Failed to update model materials:", error);
      setErrorMessage(
        error?.response?.data?.message ||
          error?.message ||
          "Could not save material settings. Please try again."
      );
    } finally {
      setSaving(false);
    }
  };

  if (!modal) return null;

  return ReactDOM.createPortal(
    <>
      <div
        className="modal fade show organ-material-modal"
        tabIndex="-1"
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-organ-materials-title"
        style={{ display: "block" }}
      >
        <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-lg">
          <div className="modal-content organ-material-modal__content">
            <div className="modal-header organ-material-modal__header">
              <div className="d-flex align-items-center gap-3">
                <span className="organ-material-modal__title-icon" aria-hidden="true">
                  <i className="bi bi-sliders2" />
                </span>
                <div>
                  <h2
                    className="modal-title organ-material-modal__title"
                    id="edit-organ-materials-title"
                  >
                    Edit model materials
                  </h2>
                  <p className="organ-material-modal__subtitle mb-0">
                    Rename materials and arrange their display order.
                  </p>
                </div>
              </div>
              <button
                type="button"
                className="btn-close"
                onClick={closeModal}
                aria-label="Close"
                disabled={saving}
              />
            </div>

            <div className="modal-body organ-material-modal__body">
              <div className="organ-material-modal__list-heading">
                <div>
                  <h3 className="organ-material-modal__section-title">Materials</h3>
                  <p className="organ-material-modal__hint mb-0">
                    Drag rows to change the order. Names shown here are the GLB material names.
                  </p>
                </div>
                <span className="organ-material-modal__count">
                  {materialsUI.length} {materialsUI.length === 1 ? "material" : "materials"}
                </span>
              </div>

              {errorMessage && (
                <div className="alert alert-danger py-2 mt-3 mb-0" role="alert">
                  {errorMessage}
                </div>
              )}

              {materialsUI.length > 0 ? (
                <div className="organ-material-modal__list" role="list">
                  {materialsUI.map((item, index) => (
                    <div
                      key={`${item.originalName}-${index}`}
                      className={`organ-material-modal__row${dragIndex === index ? " is-dragging" : ""}`}
                      draggable
                      onDragStart={(event) => {
                        setDragIndex(index);
                        event.dataTransfer.effectAllowed = "move";
                      }}
                      onDragOver={(event) => event.preventDefault()}
                      onDrop={() => handleDrop(index)}
                      onDragEnd={() => setDragIndex(null)}
                      role="listitem"
                    >
                      <span
                        className="organ-material-modal__drag-handle"
                        aria-label="Drag to reorder"
                        title="Drag to reorder"
                      >
                        <i className="bi bi-grip-vertical" aria-hidden="true" />
                      </span>
                      <span className="organ-material-modal__order">
                        {String(index + 1).padStart(2, "0")}
                      </span>
                      <div className="organ-material-modal__original">
                        <span className="organ-material-modal__field-label">
                          Original name
                        </span>
                        <span className="organ-material-modal__original-name" title={item.originalName}>
                          {item.originalName}
                        </span>
                      </div>
                      <label className="organ-material-modal__display">
                        <span className="organ-material-modal__field-label">
                          Display name
                        </span>
                        <input
                          type="text"
                          className="form-control"
                          value={item.displayName || ""}
                          onChange={(event) =>
                            updateDisplayName(index, event.target.value)
                          }
                          aria-label={`Display name for ${item.originalName}`}
                        />
                      </label>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="organ-material-modal__empty">
                  <i className="bi bi-box-seam" aria-hidden="true" />
                  <strong>No materials available</strong>
                  <span>There are no model materials to edit.</span>
                </div>
              )}
            </div>

            <div className="modal-footer organ-material-modal__footer">
              <button
                type="button"
                className="btn btn-light organ-material-modal__cancel"
                onClick={closeModal}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary organ-material-modal__save"
                onClick={handleSubmit}
                disabled={saving || materialsUI.length === 0}
              >
                {saving ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm"
                      aria-hidden="true"
                    />
                    Saving…
                  </>
                ) : (
                  <>
                    <i className="bi bi-check2" aria-hidden="true" />
                    Save changes
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show organ-material-modal__backdrop" />
    </>,
    document.getElementById("modal-root")
  );
}

export default EditOrganMenuModal;
